import { motion } from 'motion/react';
import { useState, useEffect } from 'react';
import styles from './RainCloud.module.scss';

export function RainCloud() {
  const [showLightning, setShowLightning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowLightning(true);
      setTimeout(() => setShowLightning(false), 150);
    }, 3000 + Math.random() * 2000);

    return () => clearInterval(interval);
  }, []);

  const raindrops = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: 10 + (i * 3) % 80,
    delay: Math.random() * 2,
    duration: 1 + Math.random() * 0.5,
    size: 2 + Math.random() * 2,
  }));

  return (
    <div className={styles.rainCloud}>
      {/* Cloud Container */}
      <div className={styles.rainCloud__container}>
        {/* Main Cloud with better shaping */}
        <svg className={styles.rainCloud__cloudSvg} width="280" height="140" viewBox="0 0 280 140">
          <defs>
            <linearGradient id="cloudGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#7f8c8d', stopOpacity: 1 }} />
              <stop offset="50%" style={{ stopColor: '#95a5a6', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#b0b6b8', stopOpacity: 1 }} />
            </linearGradient>
          </defs>

          <motion.g
            initial={{ y: -30, opacity: 0, scale: 0.8 }}
            animate={{
              y: [0, -8, 0],
              opacity: 1,
              scale: 1,
            }}
            transition={{
              y: {
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              },
              opacity: { duration: 0.6 },
              scale: { duration: 0.6, type: "spring" }
            }}
          >
            {/* Cloud puffs */}
            <ellipse cx="140" cy="80" rx="70" ry="35" fill="url(#cloudGradient)" />
            <ellipse cx="90" cy="70" rx="55" ry="32" fill="url(#cloudGradient)" />
            <ellipse cx="190" cy="70" rx="55" ry="32" fill="url(#cloudGradient)" />
            <ellipse cx="110" cy="55" rx="45" ry="28" fill="#6c7a7d" />
            <ellipse cx="170" cy="55" rx="45" ry="28" fill="#6c7a7d" />
            <ellipse cx="140" cy="45" rx="38" ry="25" fill="#5d6a6d" />

            {/* Sad face on cloud */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              {/* Eyes */}
              <ellipse cx="120" cy="60" rx="4" ry="6" fill="#4a5558" />
              <ellipse cx="160" cy="60" rx="4" ry="6" fill="#4a5558" />

              {/* Sad mouth - curved down */}
              <path
                d="M 125 75 Q 140 68 155 75"
                stroke="#4a5558"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
              />

              {/* Tear drops */}
              <motion.ellipse
                cx="122"
                cy="68"
                rx="2"
                ry="4"
                fill="#6bb6ff"
                initial={{ y: 0, opacity: 0 }}
                animate={{
                  y: [0, 12, 12],
                  opacity: [0, 1, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
              />
            </motion.g>
          </motion.g>
        </svg>

        {/* Lightning flash */}
        {showLightning && (
          <motion.div
            className={styles.rainCloud__lightning}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.15 }}
          >
            <svg width="30" height="60" viewBox="0 0 30 60">
              <path
                className={styles.rainCloud__lightningBolt}
                d="M 15 0 L 10 25 L 18 25 L 12 60 L 25 22 L 15 22 Z"
              />
              <path
                className={styles.rainCloud__lightningBoltHighlight}
                d="M 15 0 L 10 25 L 18 25 L 12 60 L 25 22 L 15 22 Z"
              />
            </svg>
          </motion.div>
        )}

        {/* Enhanced Rain */}
        <div className={styles.rainCloud__rainContainer}>
          {raindrops.map((drop) => (
            <motion.div
              key={drop.id}
              className={styles.rainCloud__raindrop}
              initial={{ y: 0, opacity: 0 }}
              animate={{
                y: [0, 400],
                opacity: [0, 0.7, 0.7, 0],
              }}
              transition={{
                duration: drop.duration,
                delay: drop.delay,
                repeat: Infinity,
                ease: "linear",
                repeatDelay: 0.3,
              }}
              style={{
                '--drop-left': `${drop.x}%`,
                '--drop-width': `${drop.size}px`,
                '--drop-height': `${drop.size * 8}px`,
              } as React.CSSProperties}
            />
          ))}

          {/* Splashes at bottom */}
          {raindrops.slice(0, 15).map((drop) => (
            <motion.div
              key={`splash-${drop.id}`}
              className={styles.rainCloud__splash}
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: [0, 1.5, 0],
                opacity: [0, 0.6, 0],
              }}
              transition={{
                duration: 0.4,
                delay: drop.delay + drop.duration,
                repeat: Infinity,
                repeatDelay: drop.duration + 0.3,
              }}
              style={{
                '--splash-left': `${drop.x}%`,
              } as React.CSSProperties}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
